import React from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from './Navbar/Navbar';
import Footer from './Footer';
import { JsonLd } from './JsonLd';

const SITE = 'https://www.cygnetcall.co.uk';
const DEFAULT_IMAGE = `${SITE}/og-image.png`;
const DEFAULT_DESC = 'UK-based call handling, virtual receptionist, and Duty of Care services. Professional, 24/7, same-day setup. Call 0208 538 5400.';

const ROUTE_LABELS = {
  '/': 'Home',
  '/about-us': 'About Us',
  '/services': 'Services',
  '/industries': 'Industries',
  '/pricing': 'Pricing',
  '/faq': 'FAQ',
  '/contact': 'Contact',
};

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': ['Organization', 'LocalBusiness'],
  name: 'Cygnet Call Centre',
  url: SITE,
  logo: `${SITE}/cygnet-logo.jpg`,
  telephone: '+442085385400',
  email: 'info@cygnetcall.co.uk',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Suite 133, 981 Great West Road',
    addressLocality: 'Brentford',
    postalCode: 'TW8 9DN',
    addressCountry: 'GB',
  },
  areaServed: {
    '@type': 'Country',
    name: 'United Kingdom',
  },
  openingHoursSpecification: {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    opens: '00:00',
    closes: '23:59',
  },
  description: DEFAULT_DESC,
};

const PageTemplate = ({
  title,
  description = DEFAULT_DESC,
  keywords,
  ogTitle,
  ogDescription,
  ogUrl,
  ogImage = DEFAULT_IMAGE,
  twitterCard = 'summary_large_image',
  twitterTitle,
  twitterDescription,
  twitterImage = DEFAULT_IMAGE,
  children,
}) => {
  const { pathname } = useLocation();

  const resolvedTitle = title || 'Cygnet Call Centre — UK Call Handling & Virtual Receptionist';
  const resolvedOgTitle = ogTitle || resolvedTitle;
  const resolvedOgDesc = ogDescription || description;
  const resolvedCanonical = ogUrl || `${SITE}${pathname}`;

  const breadcrumbSchema = pathname !== '/' ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: SITE,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: ROUTE_LABELS[pathname] || resolvedTitle,
        item: resolvedCanonical,
      },
    ],
  } : null;

  return (
    <>
      <Helmet>
        <title>{resolvedTitle}</title>
        <meta name="description" content={description} />
        {keywords && <meta name="keywords" content={keywords} />}
        <link rel="canonical" href={resolvedCanonical} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Cygnet Call Centre" />
        <meta property="og:title" content={resolvedOgTitle} />
        <meta property="og:description" content={resolvedOgDesc} />
        <meta property="og:url" content={resolvedCanonical} />
        <meta property="og:image" content={ogImage} />

        <meta name="twitter:card" content={twitterCard} />
        <meta name="twitter:title" content={twitterTitle || resolvedOgTitle} />
        <meta name="twitter:description" content={twitterDescription || resolvedOgDesc} />
        <meta name="twitter:image" content={twitterImage} />
      </Helmet>

      <JsonLd data={organizationSchema} />
      {breadcrumbSchema && <JsonLd data={breadcrumbSchema} />}

      <Navbar />
      <motion.main
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.25, ease: 'easeInOut' }}
      >
        {children}
      </motion.main>
      <Footer />
    </>
  );
};

export default PageTemplate;
